import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Drawer, IconButton, Box, Typography,
  CircularProgress, Alert, Button, Fab, Chip,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon           from "@mui/icons-material/Close";
import RefreshIcon         from "@mui/icons-material/Refresh";
import ChevronLeftIcon     from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon    from "@mui/icons-material/ChevronRight";
import { FeedItem }        from "../components/TopicViewFeed";
import DrawerSidebar       from "./DrawerSidebar";
import { useTopicPosts, useTopicStream } from "./topicHooks";
import { modelsApi }       from "../../../../../API/axiosInstance";
import { toast }           from "react-toastify";
import dayjs               from "dayjs";

const CustomDrawer = ({
  open,
  onClose,
  topicData,
  selectedIndex,
  totalCount,
  onNext,
  onPrev,
}) => {
  const [showScrollTop,      setShowScrollTop]      = useState(false);
  const [selectedPlatforms,  setSelectedPlatforms]  = useState([]);
  const [sortOption,         setSortOption]         = useState("");
  const [searchQuery,        setSearchQuery]        = useState("");
  const [fromDate,           setFromDate]           = useState(null);
  const [toDate,             setToDate]             = useState(null);
  const [dataType,           setDataType]           = useState("all");
  const [showTranslation,    setShowTranslation]    = useState(false);
  const [showPersonalized,   setShowPersonalized]   = useState(false);

  // Reports tab state (lifted so left panel can react)
  const [activeTab,          setActiveTab]          = useState(0);
  const [analysisRecords,    setAnalysisRecords]    = useState([]);
  const [analysisLoading,    setAnalysisLoading]    = useState(false);
  const [selectedBatch,      setSelectedBatch]      = useState(null);

  const scrollContainerRef = useRef(null);
  const topicId = topicData?._id;

  // ── Keep a always-fresh filters ref so hooks can read current values
  //    without filters appearing in their dependency arrays.
  const filtersRef = useRef({ selectedPlatforms, sortOption, searchQuery, fromDate, toDate, personalized: false });
  filtersRef.current = { selectedPlatforms, sortOption, searchQuery, fromDate, toDate, personalized: showPersonalized };

  // ── REST — paginated infinite scroll
  const rest = useTopicPosts({
    topicId,
    dataType,
    filtersRef,
    enabled: open && Boolean(topicId),
    topicType: topicData?.type,
  });

  // ── SSE — live prepend
  const stream = useTopicStream({
    topicId,
    dataType,
    enabled: open && Boolean(topicId),
    topicType: topicData?.type,
    personalized: showPersonalized,
  });

  // ── Merge: live posts first, REST posts deduplicated against streamed ids
  const posts = [
    ...stream.posts,
    ...rest.posts.filter((p) => !stream.streamedIds.has(p._id)),
  ];
  const totalPosts    = rest.totalCount + stream.newLiveCount;
  const loading       = rest.loading;
  const loadingMore   = rest.loadingMore;
  const error         = rest.error || stream.error;
  const platformsInfo = rest.platformsInfo;

  // ── Reset filters when the topic changes
  useEffect(() => {
    setFromDate(null);
    setToDate(null);
    setSelectedPlatforms([]);
    setSortOption("");
    setSearchQuery("");
    setDataType("all");
    setShowTranslation(false);
    setShowPersonalized(false);
    setActiveTab(0);
    setSelectedBatch(null);
    setAnalysisRecords([]);
  }, [topicId]);

  // ── Fetch analysis records when Reports tab becomes active
  useEffect(() => {
    if (activeTab !== 2 || !topicId) return;
    setAnalysisLoading(true);
    modelsApi
      .get("/analysis-records", { params: { topic_id: topicId } })
      .then((res) => setAnalysisRecords(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setAnalysisRecords([]);
        toast.error("Failed to load analysis batches.");
      })
      .finally(() => setAnalysisLoading(false));
  }, [activeTab, topicId]);

  // ── Date bounds derived from fetched analysis records (for custom date picker)
  const recordsMinDate = useMemo(() => {
    if (!analysisRecords.length) return undefined;
    return dayjs(Math.min(...analysisRecords.map((r) => dayjs(r.start_date).valueOf())));
  }, [analysisRecords]);

  const recordsMaxDate = useMemo(() => {
    if (!analysisRecords.length) return undefined;
    return dayjs(Math.max(...analysisRecords.map((r) => dayjs(r.end_date).valueOf())));
  }, [analysisRecords]);

  // ── Reset translation toggle when data type changes
  useEffect(() => {
    setShowTranslation(false);
  }, [dataType]);

  // ── Re-fetch when personalized toggle changes
  useEffect(() => {
    if (!topicId) return;
    rest.retry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPersonalized]);

  // ── Infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setShowScrollTop(scrollTop > 300);
    if (activeTab !== 2 && scrollHeight - scrollTop <= clientHeight + 100) {
      rest.loadMore();
    }
  }, [rest, activeTab]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    container?.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToTop = () =>
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  // ── Filter / sort handlers
  const handleRefresh = () => rest.retry();

  const handleApplyFilters = () => rest.retry();

  const handleClearFilters = () => {
    // Update ref immediately before retry so hook reads cleared values
    filtersRef.current = {
      selectedPlatforms: [], sortOption: "", searchQuery: "",
      fromDate: null, toDate: null,
    };
    setSearchQuery(""); setFromDate(null); setToDate(null);
    setSelectedPlatforms([]); setSortOption("");
    rest.retry();
  };

  const handleSortChange = (value) => {
    filtersRef.current = { ...filtersRef.current, sortOption: value };
    setSortOption(value);
    rest.retry();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "87%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
      ModalProps={{ keepMounted: true }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px 16px 16px",
          borderBottom: "1px solid #e0e7ff",
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Typography
            variant="h4"
            className="drawer-title"
            sx={{ lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {topicData?.topic || "N/A"}
          </Typography>

          {/* Live indicator — mirrors SessionDataDrawer */}
          {stream.connected && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 7, height: 7, borderRadius: "50%", bgcolor: "#10B981",
                  animation: "pulse 1.4s ease-in-out infinite",
                }}
              />
              {stream.newLiveCount > 0 && (
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#059669",
                        fontFamily: "Host Grotesk, sans-serif" }}
                >
                  +{stream.newLiveCount}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
          <IconButton onClick={onPrev} disabled={selectedIndex === 0}
            sx={{ backgroundColor: "#f1f5f9", color: "#64748b", width: 40, height: 40,
                  "&:hover": { backgroundColor: "#e2e8f0", transform: "scale(1.05)" },
                  transition: "all 0.2s ease" }}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={onNext} disabled={selectedIndex === totalCount - 1}
            sx={{ backgroundColor: "#f1f5f9", color: "#64748b", width: 40, height: 40,
                  "&:hover": { backgroundColor: "#e2e8f0", transform: "scale(1.05)" },
                  transition: "all 0.2s ease" }}>
            <ChevronRightIcon />
          </IconButton>
          <IconButton onClick={handleRefresh} disabled={loading}
            sx={{ backgroundColor: "#f1f5f9", color: "#64748b", width: 40, height: 40,
                  "&:hover": { backgroundColor: "#e2e8f0", transform: "scale(1.05)" },
                  transition: "all 0.2s ease" }}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose}
            sx={{ backgroundColor: "#f1f5f9", color: "#64748b", width: 40, height: 40,
                  "&:hover": { backgroundColor: "#e2e8f0", transform: "scale(1.05)" },
                  transition: "all 0.2s ease" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
        {/* Feed column — shows posts normally, or batch list when Reports tab is active */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: "0 0 75%", p: 4,
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(5px)",
            overflowY: "auto",
          }}
        >
          {/* ── Always-visible top controls row ── */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            {/* Left: data-type pills — hidden on Reports tab */}
            <Box>
              {activeTab !== 2 && (
                <Box sx={{ display: "inline-flex", backgroundColor: "#f1f5f9", borderRadius: "999px", p: "4px", gap: "3px" }}>
                  {[
                    { value: "all",       label: "All",         activeColor: "#d97706", activeBg: "#fef3c7", activeBorder: "#fcd34d" },
                    { value: "raw",       label: "Unprocessed", activeColor: "#7c3aed", activeBg: "#ede9fe", activeBorder: "#c4b5fd" },
                    { value: "processed", label: "Processed",   activeColor: "#15803d", activeBg: "#dcfce7", activeBorder: "#86efac" },
                  ].map(({ value, label, activeColor, activeBg, activeBorder }) => {
                    const isActive = dataType === value;
                    return (
                      <Box
                        key={value}
                        onClick={() => setDataType(value)}
                        sx={{
                          px: 3, py: 1, borderRadius: "999px", cursor: "pointer",
                          fontFamily: "Host Grotesk, sans-serif", fontSize: "0.9rem",
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? activeColor : "#64748b",
                          backgroundColor: isActive ? activeBg : "transparent",
                          border: isActive ? `1.5px solid ${activeBorder}` : "1.5px solid transparent",
                          transition: "all 0.2s ease", userSelect: "none",
                          "&:hover": !isActive ? { backgroundColor: "#e2e8f0", color: "#334155" } : {},
                        }}
                      >
                        {label}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Right: Personalised (always) + Translation (processed only) + count (feed only) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Personalised Results toggle — always visible */}
              <Box sx={{ display: "inline-flex", alignItems: "center", backgroundColor: "#f1f5f9", borderRadius: "999px", p: "3px", gap: "2px" }}>
                <Typography
                  variant="caption"
                  sx={{
                    pl: 1.5, pr: 0.5,
                    fontFamily: "Host Grotesk, sans-serif",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#374151",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Personalised Results
                </Typography>
                {[{ value: false, label: "Off" }, { value: true, label: "On" }].map(({ value, label }) => {
                  const isActive = showPersonalized === value;
                  return (
                    <Box
                      key={label}
                      onClick={() => setShowPersonalized(value)}
                      sx={{
                        px: 2, py: 0.6, borderRadius: "999px", cursor: "pointer",
                        fontFamily: "Host Grotesk, sans-serif", fontSize: "0.8rem",
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? "#7c3aed" : "#64748b",
                        backgroundColor: isActive ? "#ede9fe" : "transparent",
                        border: isActive ? "1.5px solid #c4b5fd" : "1.5px solid transparent",
                        transition: "all 0.2s ease", userSelect: "none",
                        "&:hover": !isActive ? { backgroundColor: "#e2e8f0", color: "#334155" } : {},
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>

              {/* Translation toggle — only on processed feed */}
              {activeTab !== 2 && dataType === "processed" && (
                <Box sx={{ display: "inline-flex", backgroundColor: "#f1f5f9", borderRadius: "999px", p: "3px", gap: "2px" }}>
                  {[{ value: false, label: "Original" }, { value: true, label: "Translated" }].map(({ value, label }) => {
                    const isActive = showTranslation === value;
                    return (
                      <Box
                        key={label}
                        onClick={() => setShowTranslation(value)}
                        sx={{
                          px: 2, py: 0.6, borderRadius: "999px", cursor: "pointer",
                          fontFamily: "Host Grotesk, sans-serif", fontSize: "0.8rem",
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? "#0369a1" : "#64748b",
                          backgroundColor: isActive ? "#e0f2fe" : "transparent",
                          border: isActive ? "1.5px solid #7dd3fc" : "1.5px solid transparent",
                          transition: "all 0.2s ease", userSelect: "none",
                          "&:hover": !isActive ? { backgroundColor: "#e2e8f0", color: "#334155" } : {},
                        }}
                      >
                        {label}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Post count — only in feed mode */}
              {activeTab !== 2 && (
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", fontFamily: "Host Grotesk, sans-serif", fontSize: "0.85rem", whiteSpace: "nowrap" }}
                >
                  {totalPosts || 0} posts
                  {stream.connected && (
                    <Box component="span" sx={{ ml: 1, color: "#10B981", fontWeight: 600 }}>· live</Box>
                  )}
                </Typography>
              )}
            </Box>
          </Box>

          {/* ── Batch list (Reports mode) ── */}
          {activeTab === 2 ? (
            <Box>
              {/* Header */}
              <Box
                sx={{
                  mb: 3,
                  pb: 2.5,
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Host Grotesk, sans-serif",
                      fontWeight: 700,
                      color: "#1e293b",
                      fontSize: "1.1rem",
                      mb: 0.4,
                    }}
                  >
                    Analysis Records
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      fontFamily: "Host Grotesk, sans-serif",
                      fontSize: "0.82rem",
                    }}
                  >
                    Select a records or use a custom date range from the sidebar
                  </Typography>
                </Box>
                {analysisRecords.length > 0 && (
                  <Chip
                    label={`${analysisRecords.length} batch${analysisRecords.length !== 1 ? "es" : ""}`}
                    size="small"
                    sx={{
                      backgroundColor: "#f1f5f9",
                      color: "#64748b",
                      fontFamily: "Host Grotesk, sans-serif",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 22,
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  />
                )}
              </Box>

              {analysisLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                  <CircularProgress sx={{ color: "#0f766e" }} />
                </Box>
              )}

              {!analysisLoading && analysisRecords.length === 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    p: 6,
                    backgroundColor: "#f8fafc",
                    borderRadius: "12px",
                    border: "1px dashed #e2e8f0",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#94a3b8",
                      fontFamily: "Host Grotesk, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    No analysis batches found for this topic.
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#cbd5e1",
                      fontFamily: "Host Grotesk, sans-serif",
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    Try using the Custom Date option in the sidebar.
                  </Typography>
                </Box>
              )}

              {!analysisLoading && analysisRecords.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {analysisRecords.map((record) => {
                    const isSelected = selectedBatch?._id === record._id;
                    const sameDay =
                      dayjs(record.start_date).format("YYYY-MM-DD") ===
                      dayjs(record.end_date).format("YYYY-MM-DD");
                    const isCompleted = record.summary_status === "completed";

                    return (
                      <Box
                        key={record._id}
                        onClick={() => setSelectedBatch(isSelected ? null : record)}
                        sx={{
                          backgroundColor: isSelected ? "#f7fffe" : "#ffffff",
                          border: isSelected
                            ? "2px solid #0f766e"
                            : "1.5px solid #e5e7eb",
                          borderRadius: "14px",
                          cursor: "pointer",
                          transition: "all 0.22s ease",
                          overflow: "hidden",
                          boxShadow: isSelected
                            ? "0 6px 20px rgba(15, 118, 110, 0.14)"
                            : "0 2px 8px rgba(15, 23, 42, 0.06)",
                          "&:hover": {
                            borderColor: "#0f766e",
                            boxShadow: "0 6px 20px rgba(15, 118, 110, 0.14)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >

                        {/* Card body */}
                        <Box sx={{ p: 3 }}>
                          {/* Row 1: Topic name + status badge */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 700,
                                color: "#1e293b",
                                fontFamily: "Host Grotesk, sans-serif",
                                fontSize: "0.95rem",
                                lineHeight: 1.3,
                                flex: 1,
                                mr: 1,
                              }}
                            >
                              {record.topic}
                            </Typography>
                            <Box
                              sx={{
                                px: 1.2,
                                py: 0.35,
                                borderRadius: "6px",
                                backgroundColor: isCompleted ? "#dcfce7" : "#fef3c7",
                                border: `1px solid ${isCompleted ? "#bbf7d0" : "#fde68a"}`,
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isCompleted ? "#15803d" : "#d97706",
                                  fontFamily: "Host Grotesk, sans-serif",
                                  fontWeight: 700,
                                  fontSize: "0.65rem",
                                  textTransform: "capitalize",
                                }}
                              >
                                {record.summary_status}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Row 2: Date range — styled like a timestamp */}
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                              px: 1.5,
                              py: 0.6,
                              backgroundColor: isSelected ? "#f0fdfa" : "#f8fafc",
                              border: `1px solid ${isSelected ? "#99f6e4" : "#e2e8f0"}`,
                              borderRadius: "8px",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: isSelected ? "#0f766e" : "#94a3b8",
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: isSelected ? "#0f766e" : "#475569",
                                fontFamily: "Host Grotesk, sans-serif",
                                fontSize: "0.83rem",
                                letterSpacing: "0.01em",
                              }}
                            >
                              {sameDay
                                ? dayjs(record.start_date).format("DD MMM YYYY")
                                : `${dayjs(record.start_date).format("DD MMM YYYY")} — ${dayjs(record.end_date).format("DD MMM YYYY")}`}
                            </Typography>
                          </Box>

                          {/* Row 3: Stats footer */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              pt: 1.5,
                              borderTop: "1px solid #f1f5f9",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 800,
                                  color: "#0f766e",
                                  fontFamily: "Host Grotesk, sans-serif",
                                  fontSize: "1rem",
                                  lineHeight: 1,
                                }}
                              >
                                {record.post_count.toLocaleString()}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  fontFamily: "Host Grotesk, sans-serif",
                                  fontSize: "0.75rem",
                                }}
                              >
                                posts
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#94a3b8",
                                fontFamily: "Host Grotesk, sans-serif",
                                fontSize: "0.72rem",
                              }}
                            >
                              {dayjs(record.createdAt).format("DD MMM YYYY")}
                            </Typography>
                          </Box>
                        </Box>



                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ) : (
            /* ── Normal posts feed ── */
            <Box>

              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress sx={{ color: "#0f766e" }} />
                </Box>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, fontFamily: "Host Grotesk, sans-serif", backgroundColor: "#fef2f2",
                        borderRadius: "12px", border: "1px solid #fecaca" }}
                  action={
                    <Button size="small" onClick={rest.retry}
                      sx={{ fontFamily: "Host Grotesk, sans-serif", color: "#dc2626", textTransform: "none" }}>
                      Retry
                    </Button>
                  }
                >
                  {error}
                </Alert>
              )}

              {!loading && !error && posts.length > 0 && (
                <Box>
                  {posts.map((item, index) => (
                    <FeedItem
                      key={item._id || `${item._id}-${index}`}
                      item={item}
                      index={index}
                      showTranslation={showTranslation}
                      topicType={topicData?.type}
                      isStreamed={stream.streamedIds.has(item._id)}
                    />
                  ))}

                  {loadingMore && (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                      <CircularProgress size={30} sx={{ color: "#0f766e" }} />
                      <Typography variant="body2" sx={{ ml: 2, color: "#64748b" }}>
                        Loading more posts…
                      </Typography>
                    </Box>
                  )}

                  {!rest.hasMore && (
                    <Box sx={{ textAlign: "center", p: 3 }}>
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        You've reached the end of all posts
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {!loading && !error && posts.length === 0 && (
                <Box sx={{ textAlign: "center", p: 4 }}>
                  <Typography variant="h6" sx={{ color: "#64748b", mb: 2 }}>
                    No posts available
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Sidebar */}
        <DrawerSidebar
          key={topicId}
          topicData={topicData}
          loading={loading}
          totalPosts={totalPosts}
          feedData={posts}
          dateRange={rest.dateRange}
          platformsInfo={platformsInfo}
          sortOption={sortOption}
          setSortOption={setSortOption}
          selectedPlatforms={selectedPlatforms}
          setSelectedPlatforms={setSelectedPlatforms}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          onSortChange={handleSortChange}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          selectedBatch={selectedBatch}
          onSelectBatch={setSelectedBatch}
          recordsMinDate={recordsMinDate}
          recordsMaxDate={recordsMaxDate}
        />
      </Box>

      {showScrollTop && (
        <Fab
          onClick={scrollToTop}
          sx={{
            position: "fixed", bottom: 24,
            right: { xs: 24, sm: 240, md: 280, lg: 320, xl: 420 },
            backgroundColor: "#0f766e", color: "white",
            "&:hover": { backgroundColor: "#0d5b57", transform: "scale(1.1)" },
            transition: "all 0.3s ease", zIndex: 1300,
            boxShadow: "0 4px 12px rgba(15,118,110,0.3)",
          }}
          size="medium"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </Drawer>
  );
};

export default CustomDrawer;
